// Én knapp på forsiden: ikon, tittel og en kort forklaring. Stor øverst, liten under «Mer».
// Den fremhevede (Gjøremål) er egen komponent, Gjoremal.jsx.
import React from 'react';
import { Link } from 'react-router-dom';
import Ikon from './Ikon';
import { FARGER, STI, finnHandling } from './handlinger';
import { GAMMEL_ETTER } from '../utils/mineOrdrer';

// Ordrer: antall åpne ordrer på den valgte ansatte, med varseltrekant når noen er eldre enn
// GAMMEL_ETTER virkedager. Gult tall, ikke rødt, fordi det ikke er «nye» ting.
function OrdreTeller({ antall, gamle, plassering }) {
  if (!antall) return null;
  return (
    <span className={`${plassering} flex h-7 shrink-0 items-center gap-1 rounded-full px-2 text-sm font-bold ${gamle ? 'bg-red-700 text-white' : 'bg-amber-200 text-amber-950'}`}>
      {gamle > 0 && <Ikon navn="varsel" className="w-4 h-4" />}
      {antall}
      <span className="sr-only">
        {` ${antall === 1 ? 'aktiv ordre' : 'aktive ordrer'} på deg`}
        {gamle > 0 && `, ${gamle} eldre enn ${GAMMEL_ETTER} virkedager`}
      </span>
    </span>
  );
}

export default function Knapp({ handling, stor = false, teller = 0 }) {
  const farge = FARGER[handling.farge] || FARGER.info;
  const til = handling.til || `${STI}/${handling.id}`;
  const ordre = typeof teller === 'object' && teller ? teller : null;
  const tellerTekst = (plassering) => (ordre ? <OrdreTeller {...ordre} plassering={plassering} /> : teller > 0 ? (
    <span className={`${plassering} flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-full bg-red-600 px-2 text-sm font-bold text-white`}>
      {teller}<span className="sr-only"> nye</span>
    </span>
  ) : null);

  // Ikke tatt i bruk ennå: skravert, uten lenke, så den ikke kan trykkes eller nås med Tab.
  if (handling.ikkeIBruk) {
    return (
      <div
        className={`flex h-full cursor-not-allowed rounded-2xl border-2 border-dashed border-slate-300 text-slate-700 ${
          stor ? 'min-h-[7.5rem] flex-col gap-2 p-3' : 'min-h-[2.75rem] items-center gap-2 rounded-xl p-2.5'
        }`}
        style={{ backgroundImage: 'repeating-linear-gradient(135deg, #f1f5f9 0 10px, #e2e8f0 10px 20px)' }}
      >
        <span className={`flex shrink-0 items-center justify-center rounded-xl bg-slate-400 text-white ${stor ? 'h-11 w-11' : 'h-10 w-10 rounded-lg'}`}>
          <Ikon navn={handling.ikon} className={stor ? 'w-6 h-6' : 'w-6 h-6'} />
        </span>
        <span className="min-w-0">
          <span className={`block font-bold leading-tight text-slate-700 ${stor ? 'text-base' : 'text-sm'}`}>{handling.tittel}</span>
          {stor && handling.hjelp && <span className="mt-1 block text-sm leading-snug text-slate-700">{handling.hjelp}</span>}
          {/* Liten knapp: merket under tittelen, så tittelen ikke presses sammen. */}
          {!stor && (
            <span className="mt-1 inline-block rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-300">
              Ikke i bruk ennå
            </span>
          )}
        </span>
        {stor && (
          <span className="mt-auto self-start shrink-0 rounded-full bg-white px-2.5 py-0.5 text-sm font-semibold text-slate-800 ring-1 ring-slate-300">
            Ikke i bruk ennå
          </span>
        )}
      </div>
    );
  }

  // Små lenker øverst til høyre (handling.hjorne), under hverandre: f.eks. «Sjekk kapasitet» i
  // «Registrer service», og «Trykkeguide» og «Klubbinfo» i «Registrer trykk». Teksten er
  // hjørnehandlingens kortTittel (ellers tittel). Ligger ved siden av hovedlenken (ikke inni den),
  // så lenkene ikke er nøstet.
  const hjorner = stor && handling.hjorne ? [].concat(handling.hjorne).map(finnHandling).filter(Boolean) : [];
  if (hjorner.length > 0) {
    return (
      <div className="relative h-full">
        <Knapp handling={{ ...handling, hjorne: undefined }} stor teller={teller} />
        <div className="absolute right-2 top-2 flex flex-col items-end gap-1.5">
          {hjorner.map((hjorne) => (
            <Link
              key={hjorne.id}
              to={hjorne.til || `${STI}/${hjorne.id}`}
              aria-label={hjorne.tittel}
              title={hjorne.tittel}
              className="inline-flex min-h-[2.25rem] min-w-[2.25rem] items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 lg:px-3"
            >
              <Ikon navn={hjorne.ikon} className="w-5 h-5 lg:w-4 lg:h-4" />
              {/* Teksten bare der knappen er bred nok; på smale skjermer bare ikonet. */}
              <span className="hidden lg:inline">{hjorne.kortTittel || hjorne.tittel}</span>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (stor) {
    return (
      <Link
        to={til}
        className={`relative flex h-full min-h-[7.5rem] flex-col gap-2 rounded-2xl border-2 p-3 transition active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${farge.flate}`}
      >
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${farge.ikon}`}>
          <Ikon navn={handling.ikon} className="w-6 h-6" />
        </span>
        <span>
          <span className="block text-base font-bold leading-tight text-slate-900">{handling.tittel}</span>
          {handling.hjelp && <span className="mt-1 block text-sm leading-snug text-slate-700">{handling.hjelp}</span>}
          {ordre?.antall > 0 && (
            <span aria-hidden="true" className={`mt-1 flex items-center gap-1 text-sm font-semibold ${ordre.gamle ? 'text-red-800' : 'text-slate-800'}`}>
              {ordre.gamle > 0 && <Ikon navn="varsel" className="w-4 h-4 shrink-0" />}
              {ordre.gamle > 0
                ? `${ordre.gamle} av dine ${ordre.antall} er over ${GAMMEL_ETTER} virkedager`
                : `${ordre.antall} ${ordre.antall === 1 ? 'aktiv' : 'aktive'} på deg`}
            </span>
          )}
        </span>
        {tellerTekst('absolute right-3 top-3')}
      </Link>
    );
  }

  return (
    <Link
      to={til}
      className={`flex h-full items-center gap-2 rounded-xl border p-2.5 min-h-[2.75rem] transition active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${farge.flate}`}
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${farge.ikon}`}>
        <Ikon navn={handling.ikon} className="w-6 h-6" />
      </span>
      <span className="min-w-0 text-sm font-semibold leading-tight text-slate-900 hyphens-manual">{handling.tittel}</span>
      {tellerTekst('ml-auto')}
    </Link>
  );
}
